import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, X, AlertCircle, Loader2 } from "lucide-react";

import setAuthToken from "../../utils/setAuthToken";
import { Input, Button } from "../../design-system";

/**
 * Champ image d'un projet : saisie d'URL OU upload de fichier.
 *
 * L'upload part sur /api/projects/upload-image et range le fichier dans
 * /storage/projects/<slug>/. La valeur remontée est toujours une URL — le
 * parent n'a pas à savoir d'où elle vient.
 */
export default function ProjectImageField({ value, onChange, slug, label = "Image", compact = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);

      const formData = new FormData();
      // Le slug doit précéder le fichier : multer le lit pour choisir le dossier.
      formData.append("slug", slug || "misc");
      formData.append("image", file);

      const response = await axios.post("/api/projects/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPreviewFailed(false);
      onChange(response.data.url);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.message || "L'upload a échoué");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          type="url"
          placeholder="https://… ou upload →"
          value={value || ""}
          aria-label={`${label} — URL`}
          onChange={(e) => {
            setPreviewFailed(false);
            setError(null);
            onChange(e.target.value);
          }}
        />
        <Button
          type="button"
          variant="secondary"
          isLoading={uploading}
          onClick={() => inputRef.current?.click()}
          leftIcon={uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.8} />}
        >
          {uploading ? "Envoi…" : "Upload"}
        </Button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="sr-only"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {error && (
        <div className="flex items-start gap-2 p-2.5 rounded-md bg-danger-bg text-danger text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
          {error}
        </div>
      )}

      {value && !previewFailed && (
        <div className="relative group">
          <img
            src={value}
            alt="Aperçu"
            onError={() => setPreviewFailed(true)}
            className={`w-full object-cover rounded-md border border-border ${compact ? "max-h-24" : "max-h-40"}`}
          />
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Retirer l'image"
            className="absolute top-1.5 right-1.5 p-1 rounded-md bg-ink/80 text-cream opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      )}

      {value && previewFailed && (
        <div className="flex items-start gap-2 p-2.5 rounded-md bg-warn-bg text-warn text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
          Cette URL ne charge pas — la page affichera le dégradé de repli.
        </div>
      )}
    </div>
  );
}
